/**
 * /payroll-l2/workers-comp — WC policy CRUD + per-pay-run accrual summary.
 *
 * Two sections:
 *   1. Policies — carrier, policy number, EMR, effective dates (CRUD)
 *   2. Accrual Summary — pick a pay run, see per-paycheck NCCI/hours/rate/EMR/$
 */
import { createFileRoute } from "@tanstack/react-router";
import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";

import { api } from "@/lib/api";
import { HelpHint } from "@/components/HelpHint";

export const Route = createFileRoute("/_auth/payroll-l2/workers-comp")({
  component: WorkersCompPage,
});

// ── Types ──────────────────────────────────────────────────────────────────

interface WCPolicy {
  id: string;
  entity_id: string;
  carrier_name: string;
  policy_number: string | null;
  emr: string;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
}

interface WCAccrualLine {
  paycheck_id: string;
  employee_id: string;
  craft_id: string | null;
  ncci_code: string | null;
  hours: string | null;
  rate_per_hour: string;
  emr: string;
  accrual: string;
}

interface WCAccrualSummary {
  pay_run_id: string;
  policy_id: string | null;
  emr: string;
  total_hours: string;
  total_accrual: string;
  lines: WCAccrualLine[];
}

interface PayRun {
  id: string;
  period_start: string;
  period_end: string;
}

// ── Helpers ────────────────────────────────────────────────────────────────

function fmtMoney(v: string | null | undefined) {
  if (v == null) return "—";
  return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(Number(v));
}

function fmtNumber(v: string | null | undefined, digits = 2) {
  if (v == null) return "—";
  return Number(v).toFixed(digits);
}

function todayPlus(years: number) {
  const d = new Date();
  d.setFullYear(d.getFullYear() + years);
  return d.toISOString().slice(0, 10);
}

// ── Page ───────────────────────────────────────────────────────────────────

function WorkersCompPage() {
  const qc = useQueryClient();
  const [editing, setEditing] = useState<WCPolicy | null>(null);
  const [adding, setAdding] = useState(false);
  const [payRunId, setPayRunId] = useState<string>("");

  const policiesQ = useQuery({
    queryKey: ["wc-policies"],
    queryFn: async () => api<WCPolicy[]>("/api/wc-policies"),
  });

  const payRunsQ = useQuery({
    queryKey: ["wc-pay-runs"],
    queryFn: async () => api<PayRun[]>("/api/payroll/pay-runs?limit=20"),
  });

  const summaryQ = useQuery({
    queryKey: ["wc-accrual-summary", payRunId],
    queryFn: async () =>
      api<WCAccrualSummary>(`/api/wc-policies/accrual-summary/${payRunId}`),
    enabled: !!payRunId,
  });

  const deleteMut = useMutation({
    mutationFn: async (id: string) =>
      api<void>(`/api/wc-policies/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["wc-policies"] }),
  });

  const policies = policiesQ.data ?? [];
  const today = new Date().toISOString().slice(0, 10);
  const activePolicy = policies.find(
    (p) =>
      p.effective_from <= today &&
      (!p.effective_to || p.effective_to >= today),
  );

  return (
    <Box>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Typography variant="h5" fontWeight={600}>
          Workers Comp
        </Typography>
        <HelpHint docPath="payroll-l2/workers-comp.md" label="Workers comp help" />
      </Stack>

      {/* Active policy summary */}
      <Paper variant="outlined" sx={{ p: 2, mb: 4 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
          <Box>
            <Typography variant="overline" color="text.secondary">
              Active policy
            </Typography>
            {activePolicy ? (
              <Stack direction="row" gap={4} mt={1}>
                <Box>
                  <Typography variant="body2" color="text.secondary">Carrier</Typography>
                  <Typography variant="body1">{activePolicy.carrier_name}</Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Policy #</Typography>
                  <Typography variant="body1" sx={{ fontFamily: "monospace" }}>
                    {activePolicy.policy_number ?? "—"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">EMR</Typography>
                  <Typography variant="body1" fontWeight={600}>
                    {fmtNumber(activePolicy.emr, 4)}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="body2" color="text.secondary">Through</Typography>
                  <Typography variant="body1">
                    {activePolicy.effective_to ?? "open-ended"}
                  </Typography>
                </Box>
              </Stack>
            ) : (
              <Alert severity="warning" sx={{ mt: 1 }}>
                No active WC policy. Accruals will use EMR = 1.0000.
              </Alert>
            )}
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => setAdding(true)}
          >
            New Policy
          </Button>
        </Stack>
      </Paper>

      {/* Policies table */}
      <Typography variant="subtitle1" fontWeight={500} mb={1}>
        All policies
      </Typography>
      {policiesQ.isLoading && <CircularProgress size={24} />}
      {policies.length === 0 && !policiesQ.isLoading && (
        <Alert severity="info">No policies yet — create one to start tracking WC.</Alert>
      )}
      {policies.length > 0 && (
        <Table size="small" sx={{ mb: 4 }}>
          <TableHead>
            <TableRow>
              <TableCell>Carrier</TableCell>
              <TableCell>Policy #</TableCell>
              <TableCell align="right">EMR</TableCell>
              <TableCell>Effective From</TableCell>
              <TableCell>Effective To</TableCell>
              <TableCell>Notes</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {policies.map((p) => (
              <TableRow key={p.id} hover>
                <TableCell>{p.carrier_name}</TableCell>
                <TableCell sx={{ fontFamily: "monospace" }}>{p.policy_number ?? "—"}</TableCell>
                <TableCell align="right">{fmtNumber(p.emr, 4)}</TableCell>
                <TableCell>{p.effective_from}</TableCell>
                <TableCell>{p.effective_to ?? "—"}</TableCell>
                <TableCell sx={{ maxWidth: 220, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                  {p.notes ?? "—"}
                </TableCell>
                <TableCell align="right">
                  <Tooltip title="Edit">
                    <IconButton size="small" onClick={() => setEditing(p)}>
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      onClick={() => {
                        if (window.confirm(`Delete WC policy ${p.carrier_name}?`)) {
                          deleteMut.mutate(p.id);
                        }
                      }}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      {/* Accrual summary */}
      <Typography variant="subtitle1" fontWeight={500} mb={1}>
        Accrual summary by pay run
      </Typography>
      <Stack direction="row" alignItems="center" gap={2} mb={2}>
        <FormControl size="small" sx={{ minWidth: 280 }}>
          <InputLabel>Pay Run</InputLabel>
          <Select
            value={payRunId}
            label="Pay Run"
            onChange={(e) => setPayRunId(e.target.value)}
          >
            <MenuItem value="">
              <em>Select a pay run</em>
            </MenuItem>
            {payRunsQ.data?.map((r) => (
              <MenuItem key={r.id} value={r.id}>
                {r.period_start} — {r.period_end}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {summaryQ.isLoading && <CircularProgress size={24} />}
      {summaryQ.data && (
        <>
          <Stack direction="row" gap={4} mb={2}>
            <KPI label="Total hours" value={fmtNumber(summaryQ.data.total_hours)} />
            <KPI label="EMR applied" value={fmtNumber(summaryQ.data.emr, 4)} />
            <KPI label="Total accrual" value={fmtMoney(summaryQ.data.total_accrual)} accent />
          </Stack>
          {summaryQ.data.lines.length === 0 ? (
            <Alert severity="info">No WC accrual lines on this pay run.</Alert>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Paycheck</TableCell>
                  <TableCell>NCCI</TableCell>
                  <TableCell align="right">Hours</TableCell>
                  <TableCell align="right">Rate</TableCell>
                  <TableCell align="right">EMR</TableCell>
                  <TableCell align="right">Accrual</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {summaryQ.data.lines.map((ln) => (
                  <TableRow key={ln.paycheck_id} hover>
                    <TableCell sx={{ fontFamily: "monospace", fontSize: 11 }}>
                      {ln.paycheck_id.slice(0, 8)}
                    </TableCell>
                    <TableCell sx={{ fontFamily: "monospace" }}>{ln.ncci_code ?? "—"}</TableCell>
                    <TableCell align="right">{fmtNumber(ln.hours)}</TableCell>
                    <TableCell align="right">{fmtNumber(ln.rate_per_hour, 4)}</TableCell>
                    <TableCell align="right">{fmtNumber(ln.emr, 4)}</TableCell>
                    <TableCell align="right">{fmtMoney(ln.accrual)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </>
      )}

      {/* Add / Edit dialog */}
      <PolicyDialog
        open={adding || !!editing}
        policy={editing}
        onClose={() => {
          setAdding(false);
          setEditing(null);
        }}
        onSaved={() => {
          qc.invalidateQueries({ queryKey: ["wc-policies"] });
          setAdding(false);
          setEditing(null);
        }}
      />
    </Box>
  );
}

// ── KPI tile ───────────────────────────────────────────────────────────────

function KPI({ label, value, accent }: { label: string; value: string; accent?: boolean }) {
  return (
    <Paper variant="outlined" sx={{ px: 3, py: 1.5, minWidth: 140 }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography
        variant="h6"
        fontWeight={600}
        sx={{ color: accent ? "primary.main" : "text.primary" }}
      >
        {value}
      </Typography>
    </Paper>
  );
}

// ── Add / Edit dialog ──────────────────────────────────────────────────────

function PolicyDialog({
  open,
  policy,
  onClose,
  onSaved,
}: {
  open: boolean;
  policy: WCPolicy | null;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [carrier, setCarrier] = useState(policy?.carrier_name ?? "");
  const [policyNum, setPolicyNum] = useState(policy?.policy_number ?? "");
  const [emr, setEmr] = useState(policy?.emr ?? "1.0000");
  const [from, setFrom] = useState(policy?.effective_from ?? new Date().toISOString().slice(0, 10));
  const [to, setTo] = useState(policy?.effective_to ?? todayPlus(1));
  const [notes, setNotes] = useState(policy?.notes ?? "");

  // Reset form when the editing target changes.
  const targetId = policy?.id ?? "new";
  const [seedId, setSeedId] = useState(targetId);
  if (open && seedId !== targetId) {
    setSeedId(targetId);
    setCarrier(policy?.carrier_name ?? "");
    setPolicyNum(policy?.policy_number ?? "");
    setEmr(policy?.emr ?? "1.0000");
    setFrom(policy?.effective_from ?? new Date().toISOString().slice(0, 10));
    setTo(policy?.effective_to ?? todayPlus(1));
    setNotes(policy?.notes ?? "");
  }

  const saveMut = useMutation({
    mutationFn: async () => {
      const body = {
        carrier_name: carrier,
        policy_number: policyNum || null,
        emr,
        effective_from: from,
        effective_to: to || null,
        notes: notes || null,
      };
      if (policy) {
        return api(`/api/wc-policies/${policy.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
          headers: { "Content-Type": "application/json" },
        });
      }
      return api(`/api/wc-policies`, {
        method: "POST",
        body: JSON.stringify(body),
        headers: { "Content-Type": "application/json" },
      });
    },
    onSuccess: onSaved,
  });

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
      <DialogTitle>{policy ? "Edit WC Policy" : "New WC Policy"}</DialogTitle>
      <DialogContent>
        <Stack gap={2} mt={1}>
          <TextField
            label="Carrier name"
            required
            fullWidth
            value={carrier}
            onChange={(e) => setCarrier(e.target.value)}
            placeholder="e.g. Zurich North America"
          />
          <TextField
            label="Policy number"
            fullWidth
            value={policyNum}
            onChange={(e) => setPolicyNum(e.target.value)}
          />
          <TextField
            label="EMR (Experience Mod Rate)"
            required
            fullWidth
            value={emr}
            onChange={(e) => setEmr(e.target.value)}
            helperText="From your carrier's NCCI rating worksheet, e.g. 0.92"
            inputProps={{ inputMode: "decimal" }}
          />
          <Stack direction="row" gap={2}>
            <TextField
              label="Effective from"
              type="date"
              required
              fullWidth
              value={from}
              onChange={(e) => setFrom(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
            <TextField
              label="Effective to (blank = open)"
              type="date"
              fullWidth
              value={to}
              onChange={(e) => setTo(e.target.value)}
              InputLabelProps={{ shrink: true }}
            />
          </Stack>
          <TextField
            label="Notes"
            fullWidth
            multiline
            minRows={2}
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
          {saveMut.isError && (
            <Alert severity="error">
              {(saveMut.error as Error)?.message ?? "Failed to save policy"}
            </Alert>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          disabled={!carrier || !emr || !from || saveMut.isPending}
          onClick={() => saveMut.mutate()}
        >
          {saveMut.isPending ? "Saving…" : policy ? "Save" : "Create"}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
